if (!customElements.get('delivery-promotion-notice')) {
  /**
   * Cart delivery offer notice. Asks the Tilemall app which delivery offer the
   * cart meets and what the next step is, using the same rules as checkout.
   * The server-rendered fallback text stays when the request fails.
   */
  class DeliveryPromotionNotice extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('.js-delivery-promotion-text');
      this.barEls = this.querySelectorAll('.free-shipping-notice__bar');
      this.locationHandler = this.locationHandler || (() => this.refresh());
      this.quoteHandler = this.quoteHandler || ((event) => this.applyQuote(event.detail));
      window.addEventListener('tilemall:location:change', this.locationHandler);
      window.addEventListener('tilemall:shipping:promotion', this.quoteHandler);
      this.refresh();
    }

    disconnectedCallback() {
      window.removeEventListener('tilemall:location:change', this.locationHandler);
      window.removeEventListener('tilemall:shipping:promotion', this.quoteHandler);
      this.requestId = (this.requestId || 0) + 1;
    }

    /** Use the calculator's current server result, without evaluating offers again. */
    applyQuote(detail) {
      if (!/^\d{4}$/.test(detail?.postcode || '') || !detail?.promotion
        || detail.promotion.unavailable || !Array.isArray(detail.cart?.lines)) return;
      let cart;
      try {
        cart = JSON.parse(this.dataset.cart);
      } catch (error) {
        return;
      }
      if (cart?.subtotal !== detail.cart.subtotal
        || JSON.stringify(cart.lines) !== JSON.stringify(detail.cart.lines)) return;
      // Retire an older standalone notice request, including a delayed JSON body.
      this.requestId = (this.requestId || 0) + 1;
      this.render(detail.promotion, cart.subtotal);
    }

    /**
     * Postcode the customer saved through the delivery estimator, if still valid.
     * @returns {string|undefined} Four-digit postcode.
     */
    savedPostcode() {
      let location = null;
      try {
        location = window.theme?.storageUtil?.get
          ? window.theme.storageUtil.get('tilemall-location', true)
          : JSON.parse(localStorage.getItem('tilemall-location'));
      } catch (error) {
        location = null;
      }
      if (!location?.postcode || (location.expiresAt && location.expiresAt < Date.now())) return undefined;
      return /^\d{4}$/.test(location.postcode) ? location.postcode : undefined;
    }

    async refresh() {
      this.requestId = (this.requestId || 0) + 1;
      const requestId = this.requestId;
      let cart;
      try {
        cart = JSON.parse(this.dataset.cart);
      } catch (error) {
        return;
      }
      if (!cart?.lines?.length) return;

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ lines: cart.lines, subtotal: cart.subtotal, postcode: this.savedPostcode() })
        });
        if (!response.ok || requestId !== this.requestId) return;
        const result = await response.json();
        // Parsing the body is asynchronous too: the location/cart may have changed.
        if (requestId !== this.requestId) return;
        this.render(result, cart.subtotal);
      } catch (error) {
        console.debug('Delivery offer was not loaded.', error); // eslint-disable-line
      }
    }

    /**
     * @param {object} result - Endpoint response.
     * @param {number} subtotal - Cart subtotal in dollars.
     */
    render(result, subtotal) {
      const messages = [];
      if (result.applied) messages.push(result.applied.message);
      if (result.next) messages.push(result.next.message);
      else if (!result.applied && result.regionHint) messages.push(result.regionHint.message);
      if (messages.length === 0) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.textEl.textContent = messages.join(' · ');
      let progress = result.applied ? 100 : 0;
      if (result.next) progress = Math.min(100, (subtotal / (subtotal + result.next.remaining)) * 100);
      this.barEls.forEach((bar) => bar.style.setProperty('--progress', `${progress.toFixed(1)}%`));
    }
  }

  customElements.define('delivery-promotion-notice', DeliveryPromotionNotice);
}
